import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import type { LatLon, NotificationsResponse, SavedView, Sighting, SpeciesCode, Track, ViewingLocation } from '@whaletail/shared';
import { MAX_SAVED_VIEWS } from '@whaletail/shared';
import {
  adminQueue,
  adminUnplacedSms,
  ago,
  dayEnd,
  dayStart,
  fetchHealth,
  fetchTracks,
  fetchViewingLocations,
  getNotifications,
  listViews,
  formatDateKey,
  toDateKey,
  trailLabel,
  type Health,
  type ViewSnapshot,
} from './api.ts';
import { useAuth } from './auth.ts';
import { AdminPage } from './components/AdminPage.tsx';
import type { PickedLocation } from './components/LocationPicker.tsx';
import { LoginGate } from './components/LoginGate.tsx';
import { PasswordExpiredGate } from './components/PasswordExpiredGate.tsx';
import { PlacesPanel } from './components/PlacesPanel.tsx';
import { ReportLog } from './components/ReportLog.tsx';
import { TimeControls, type TimeMode } from './components/TimeControls.tsx';
import { isInMapArea, useGeolocation } from './geolocation.ts';
import { resumeAfterSignIn } from './push.ts';
import { PersonIcon } from './icons.tsx';
import { MapView, type MapCamera, type MapHandle } from './MapView.tsx';

const TRAIL_OPTIONS = [3, 6, 12, 24, 48, 120] as const;
/**
 * Sightings are reported almost entirely in daylight, so any window shorter
 * than a day is routinely empty first thing in the morning — in live data, the
 * last 12 h held 1 report while the last 24 h held 34. 12 h is the compromise:
 * short enough to mean "recent", long enough to usually show something, and the
 * empty state offers a one-click jump to the nearest window that has data.
 */
const DEFAULT_TRAIL_HOURS = 12;
const REFRESH_MS = 60_000;
/** Vertical travel, in px, that counts as a swipe on the mobile panel handle. */
const SWIPE_PX = 24;
/** Movement under this many px is still a tap, not a drag. */
const TAP_SLOP_PX = 6;
const HOUR = 3_600_000;

type Tab = 'reports' | 'places';

/**
 * `t.linePoints` is the real points plus land-routing detour waypoints
 * between them, built server-side as one continuous run (see BuiltTrack's
 * doc comment in tracks.ts). Clipping `points` to a window is not enough —
 * `linePoints` has to be clipped to match, or the drawn trail still runs the
 * full original route past wherever the window cuts off, including stretches
 * that haven't happened yet on a scrubbed-back timeline.
 *
 * There's no water-routing on the client to rebuild a fresh detour for the
 * clipped endpoints, so this instead locates where the kept real points
 * (`points[keepStart]`..`points[keepEnd]`) land inside the existing
 * `linePoints` array and slices between them. A real point's coordinates are
 * copied verbatim into `linePoints` (buildLinePoints in tracks.ts), so exact
 * equality reliably finds them in order; falling back to the bare kept
 * points is only a safety net if that ever fails to match up.
 */
export function clipLinePoints(t: Track, keepStart: number, keepEnd: number): LatLon[] {
  const bare = t.points.slice(keepStart, keepEnd + 1).map((p) => ({ lat: p.lat, lon: p.lon }));
  if (t.linePoints.length === 0) return bare;

  let pi = 0; // walks t.points from 0, looking for each real point in linePoints order
  let startIdx = -1;
  let endIdx = -1;
  for (let li = 0; li < t.linePoints.length && pi < t.points.length; li++) {
    const lp = t.linePoints[li]!;
    const p = t.points[pi]!;
    if (lp.lat === p.lat && lp.lon === p.lon) {
      if (pi === keepStart) startIdx = li;
      if (pi === keepEnd) {
        endIdx = li;
        break;
      }
      pi++;
    }
  }
  if (startIdx === -1 || endIdx === -1) return bare;
  return t.linePoints.slice(startIdx, endIdx + 1);
}

/** Restrict each track to the points inside [from, to], dropping empties. */
export function clipTracks(tracks: Track[], from: number, to: number): Track[] {
  const out: Track[] = [];
  for (const t of tracks) {
    let keepStart = -1;
    let keepEnd = -1;
    const points: Track['points'] = [];
    for (let i = 0; i < t.points.length; i++) {
      const p = t.points[i]!;
      if (p.observedAt >= from && p.observedAt <= to) {
        if (keepStart === -1) keepStart = i;
        keepEnd = i;
        points.push(p);
      }
    }
    if (points.length === 0) continue;
    out.push({
      ...t,
      points: points.map((p, i) => ({ ...p, seq: i })),
      startedAt: points[0]!.observedAt,
      endedAt: points[points.length - 1]!.observedAt,
      linePoints: clipLinePoints(t, keepStart, keepEnd),
    });
  }
  return out;
}

export default function App() {
  const auth = useAuth();
  const geo = useGeolocation();
  const mapRef = useRef<MapHandle>(null);
  const { user } = auth;

  // --- data -----------------------------------------------------------------
  const [tracks, setTracks] = useState<Track[]>([]);
  const [health, setHealth] = useState<Health | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewingLocations, setViewingLocations] = useState<ViewingLocation[]>([]);

  // --- time -----------------------------------------------------------------
  const [timeMode, setTimeMode] = useState<TimeMode>('hours');
  const [trailHours, setTrailHours] = useState<number>(DEFAULT_TRAIL_HOURS);
  const [live, setLive] = useState(true);
  const [scrubAt, setScrubAt] = useState<number | null>(null);
  const [dateKey, setDateKey] = useState(() => toDateKey(Date.now()));
  const [now, setNow] = useState(() => Date.now());

  // --- display --------------------------------------------------------------
  const [hidden, setHidden] = useState<Set<SpeciesCode>>(new Set());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showViewing, setShowViewing] = useState(false);

  // --- ui -------------------------------------------------------------------
  const [tab, setTab] = useState<Tab>('reports');
  // On the narrow (mobile) layout the side panel lives at the bottom of the
  // screen and defaults to collapsed — just the tab headings — so it doesn't
  // eat into the map. Desktop ignores this entirely (see the .main[data-panel]
  // rules, scoped inside the mobile media query); this state simply has no
  // visual effect at desktop widths.
  const [panelOpen, setPanelOpen] = useState(false);
  const [notice, setNotice] = useState<{ id: number; text: string } | null>(null);
  const [draft, setDraft] = useState<PickedLocation | null>(null);
  const [picking, setPicking] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const isAdmin = user?.role === 'admin';
  const retentionHours = health?.retentionHours ?? 120;

  const notify = useCallback((text: string) => setNotice({ id: Date.now(), text }), []);
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 6_000);
    return () => clearTimeout(t);
  }, [notice]);

  // --- loading --------------------------------------------------------------
  const load = useCallback(async () => {
    try {
      const [t, h] = await Promise.all([fetchTracks(retentionHours), fetchHealth()]);
      setTracks(t);
      setHealth(h);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not reach the server');
    } finally {
      setLoading(false);
    }
  }, [retentionHours]);

  // Gated on user: the app requires an account (see the LoginGate branch
  // below), so nothing should be fetched before one exists — not just kept
  // off-screen. user is deliberately in the dep array so signing in (user
  // going null -> set) fires this immediately, without waiting on anything
  // else to change.
  useEffect(() => {
    if (!user) return;
    void load();
  }, [load, user]);

  const loadViewing = useCallback(() => {
    fetchViewingLocations()
      .then(setViewingLocations)
      .catch(() => undefined);
  }, []);

  // The list depends on who is asking (their own pending spots, or all of them
  // for an admin), so it is re-fetched whenever the signed-in account changes.
  // Also gated on user existing at all, for the same reason as `load` above.
  useEffect(() => {
    if (!user) return;
    loadViewing();
  }, [loadViewing, user?.id, user?.role]);

  // The nearby-whale alert radius, drawn around home on the map while alerts
  // are on — i.e. while any of this account's devices has them turned on.
  // Loaded at sign-in; the Settings tab reports every change.
  const [alertRadiusMiles, setAlertRadiusMiles] = useState<number | null>(null);
  const applyAlerts = useCallback(
    (r: NotificationsResponse) => setAlertRadiusMiles(r.available && r.deviceCount > 0 ? r.settings.radiusMiles : null),
    [],
  );
  // On sign-in (and each load while signed in), first resume this device's
  // alerts if this account had them on here before signing out, then read the
  // settings, so the map's ring reflects the resumed device.
  useEffect(() => {
    if (!user) return setAlertRadiusMiles(null);
    const accountId = user.id;
    void (async () => {
      try {
        const first = await getNotifications();
        await resumeAfterSignIn(accountId, first.vapidPublicKey);
        applyAlerts(await getNotifications());
      } catch {
        /* alerts are optional; the map works without them */
      }
    })();
  }, [user?.id, applyAlerts]); // eslint-disable-line react-hooks/exhaustive-deps

  // A copy of the account's saved views, kept here (not just inside
  // PlacesPanel) so the map's own quick-access buttons can show them even
  // when the Settings tab isn't open. PlacesPanel calls this back with the
  // fresh list after any save/rename/delete/star, same idea as loadViewing.
  const [savedViews, setSavedViews] = useState<SavedView[]>([]);
  const loadSavedViews = useCallback(() => {
    if (!user) return setSavedViews([]);
    listViews().then(setSavedViews).catch(() => undefined);
  }, [user?.id]);
  useEffect(() => {
    loadSavedViews();
  }, [loadSavedViews]);

  // Keep "now" and the data fresh while following the live feed.
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    if (!user || !live || timeMode !== 'hours') return;
    const id = setInterval(() => void load(), REFRESH_MS);
    return () => clearInterval(id);
  }, [user, live, timeMode, load]);

  // The review badge on the admin page button (Settings tab): check now,
  // then every couple of minutes while an admin.
  useEffect(() => {
    if (!isAdmin) {
      setPendingCount(0);
      return;
    }
    const check = () =>
      adminQueue()
        .then((q) => setPendingCount(q.pendingCount))
        .catch(() => undefined);
    void check();
    const id = setInterval(check, 120_000);
    return () => clearInterval(id);
  }, [isAdmin]);

  // A signed-out (or just-demoted) user has no admin page to be sitting on.
  const [adminPageOpen, setAdminPageOpen] = useState(false);
  useEffect(() => {
    if (adminPageOpen && !isAdmin) setAdminPageOpen(false);
  }, [adminPageOpen, isAdmin]);

  // Header count of Orca Network texts waiting in Unplaced Texts (admin page).
  // Checked now, every couple of minutes, and again on leaving the admin page,
  // where re-running or marking a text trained changes it.
  const [unplacedCount, setUnplacedCount] = useState(0);
  useEffect(() => {
    if (!isAdmin) {
      setUnplacedCount(0);
      return;
    }
    if (adminPageOpen) return;
    const check = () =>
      adminUnplacedSms()
        .then((list) => setUnplacedCount(list.length))
        .catch(() => undefined);
    void check();
    const id = setInterval(check, 120_000);
    return () => clearInterval(id);
  }, [isAdmin, adminPageOpen]);

  // Where to point the map once it remounts after AdminPage closes — that
  // page has no map of its own (see AdminPage.tsx), so its own "Show on map"
  // closes it and comes back here instead. MapView reads this once, at
  // mount, as initialFocus; see that prop's doc comment for why a plain
  // mapRef.current?.flyTo(...) can't work here (the ref is null the whole
  // time AdminPage is showing — MapView isn't mounted at all).
  const [pendingFocus, setPendingFocus] = useState<MapCamera | null>(null);
  const showOnMap = (lat: number, lon: number, zoom = 15) => {
    setPendingFocus({ lat, lon, zoom });
    setAdminPageOpen(false);
  };

  // --- location -------------------------------------------------------------
  useEffect(() => {
    if (!geo.firstFix || !geo.position) return;
    if (isInMapArea(geo.position)) mapRef.current?.flyTo(geo.position.lat, geo.position.lon, 12);
    else notify("You're outside the area this map covers, so it won't move to you.");
  }, [geo.firstFix, geo.position, notify]);

  useEffect(() => {
    if (geo.message) notify(geo.message);
  }, [geo.message, notify]);

  // --- the time window ------------------------------------------------------
  const newestPoint = useMemo(() => {
    let max = 0;
    for (const t of tracks) for (const p of t.points) if (p.observedAt > max) max = p.observedAt;
    return max;
  }, [tracks]);

  const dateMax = toDateKey(now);
  const dateMin = toDateKey(now - retentionHours * HOUR);

  const win = useMemo(() => {
    if (timeMode === 'date') {
      const start = dayStart(dateKey);
      // Today is only half over, so the day ends "now"; a past day ends at midnight.
      const end = Math.max(start + 60_000, Math.min(dayEnd(dateKey), now));
      const at = scrubAt !== null && scrubAt >= start && scrubAt <= end ? scrubAt : end;
      return { start, end: at, sliderMin: start, sliderMax: end };
    }
    const sliderMax = Math.max(newestPoint, now);
    const end = live ? sliderMax : (scrubAt ?? sliderMax);
    return { start: end - trailHours * HOUR, end, sliderMin: now - retentionHours * HOUR, sliderMax };
  }, [timeMode, dateKey, now, scrubAt, live, trailHours, newestPoint, retentionHours]);

  const fadeHours = Math.max((win.end - win.start) / HOUR, 1);

  const visible = useMemo(
    () => clipTracks(tracks, win.start, win.end).filter((t) => !hidden.has(t.species)),
    [tracks, win.start, win.end, hidden],
  );

  const livePositions = useMemo(
    () =>
      visible
        .map((t) => ({ ...t.points[t.points.length - 1]!, trackId: t.id, pointCount: t.points.length }))
        .sort((a, b) => b.observedAt - a.observedAt),
    [visible],
  );

  const logRows = useMemo(() => {
    const rows: Array<Sighting & { trackId: string }> = [];
    for (const t of visible) for (const p of t.points) rows.push({ ...p, trackId: t.id });
    return rows.sort((a, b) => b.observedAt - a.observedAt);
  }, [visible]);

  /** Reports per local day, so an empty day can point at the nearest one that has some. */
  const dayCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const t of tracks) {
      for (const p of t.points) {
        const key = toDateKey(p.observedAt);
        m.set(key, (m.get(key) ?? 0) + 1);
      }
    }
    return m;
  }, [tracks]);

  // What to offer when the current window is empty.
  const suggestion = useMemo(() => {
    if (logRows.length > 0) return null;
    if (timeMode === 'date') {
      // Prefer the most recent other day that has any reports.
      const best = [...dayCounts.keys()].filter((k) => k !== dateKey).sort().at(-1);
      return best ? { kind: 'day' as const, key: best, count: dayCounts.get(best) ?? 0 } : null;
    }
    const times = tracks.flatMap((t) => t.points.map((p) => p.observedAt));
    if (times.length === 0) return null;
    const hours = TRAIL_OPTIONS.find((h) => h > trailHours && times.some((t) => t >= win.end - h * HOUR && t <= win.end));
    return hours ? { kind: 'hours' as const, hours } : null;
  }, [logRows.length, timeMode, dayCounts, dateKey, tracks, trailHours, win.end]);

  // --- handlers -------------------------------------------------------------
  /** Switch tabs and, on the mobile layout, pull the collapsed panel up too — picking a tab implies wanting to see it. */
  const selectTab = (t: Tab) => {
    setTab(t);
    setPanelOpen(true);
  };

  /**
   * Mobile bottom sheet: dragging the handle / tab row moves the panel with
   * the finger; on release it settles open or collapsed (animated by CSS —
   * see `.main > .side` in theme.css). A tap (no travel) does nothing here, so
   * tab clicks still work. `sheetH` is the panel's live height while dragging.
   */
  const sideRef = useRef<HTMLElement | null>(null);
  const [sheetBounds, setSheetBounds] = useState({ collapsed: 64, open: 400 });
  const [sheetH, setSheetH] = useState<number | null>(null);
  useEffect(() => {
    const measure = () => {
      const side = sideRef.current;
      if (!side) return;
      const head = (side.querySelector('.side-handle')?.getBoundingClientRect().height ?? 0) +
        (side.querySelector('.tabs')?.getBoundingClientRect().height ?? 0);
      setSheetBounds({ collapsed: Math.ceil(head) + 1, open: Math.round(window.innerHeight * 0.5) });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
    // Re-run once signed in: until then the panel isn't rendered to measure.
  }, [auth.ready, !!user]); // eslint-disable-line react-hooks/exhaustive-deps
  const sheetHeight = sheetH ?? (panelOpen ? sheetBounds.open : sheetBounds.collapsed);

  // Tracked on `window` once a drag starts, not on the handle itself: a mouse
  // (or a finger that slides off the row) stops sending events to the element
  // it started on, which would strand the drag mid-way.
  const swipeProps = {
    onPointerDown: (e: ReactPointerEvent) => {
      if (!window.matchMedia('(max-width: 860px)').matches) return;
      const startY = e.clientY;
      const startH = sheetHeight;
      let moved = false;
      const clamp = (h: number) => Math.min(sheetBounds.open, Math.max(sheetBounds.collapsed, h));
      const end = (settle: boolean, y: number) => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onCancel);
        if (!moved) return;
        const dy = y - startY;
        if (settle && dy <= -SWIPE_PX) setPanelOpen(true);
        else if (settle && dy >= SWIPE_PX) setPanelOpen(false);
        setSheetH(null);
      };
      const onMove = (ev: PointerEvent) => {
        const dy = ev.clientY - startY;
        if (!moved && Math.abs(dy) < TAP_SLOP_PX) return;
        moved = true;
        setSheetH(clamp(startH - dy));
      };
      const onUp = (ev: PointerEvent) => end(true, ev.clientY);
      const onCancel = () => end(false, startY);
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onCancel);
    },
  };

  const goLive = () => {
    setTimeMode('hours');
    setLive(true);
    setScrubAt(null);
  };

  const chooseDate = (key: string) => {
    const clamped = key < dateMin ? dateMin : key > dateMax ? dateMax : key;
    setDateKey(clamped);
    setScrubAt(null);
    setLive(false);
    setTimeMode('date');
  };

  const changeMode = (m: TimeMode) => {
    if (m === 'hours') return goLive();
    setLive(false);
    setTimeMode('date');
    setScrubAt(null);
  };

  const changeTrail = (h: number) => {
    setTrailHours(h);
  };

  const scrub = (t: number) => {
    setLive(false);
    setScrubAt(t);
  };

  const flyTo = useCallback((lat: number, lon: number, zoom?: number) => mapRef.current?.flyTo(lat, lon, zoom), []);

  const captureView = useCallback((): ViewSnapshot | null => {
    const c = mapRef.current?.getCamera();
    if (!c) return null;
    return {
      lat: Number(c.lat.toFixed(5)),
      lon: Number(c.lon.toFixed(5)),
      zoom: Number(c.zoom.toFixed(2)),
      trailHours,
      showViewing,
      hiddenSpecies: [...hidden],
    };
  }, [trailHours, showViewing, hidden]);

  const applyView = (v: SavedView, auto = false) => {
    // A saved view restores where you were looking and what was drawn, not the
    // time filter: jumping to a spot shouldn't throw away the day or hour range
    // you are inspecting.
    setShowViewing(v.showViewing);
    setHidden(new Set(v.hiddenSpecies));
    if (auto) {
      // Page load: place the camera outright, so the map opens on the view
      // instead of first framing the sightings and then sliding over.
      mapRef.current?.jumpTo(v.lat, v.lon, v.zoom);
      return;
    }
    mapRef.current?.flyTo(v.lat, v.lon, v.zoom);
  };

  /**
   * The default view first (if any), then the rest — the map's own
   * quick-access buttons. Capped at MAX_SAVED_VIEWS in case an account kept
   * more from before the limit was lowered.
   */
  const quickViews = useMemo(() => {
    const def = savedViews.find((v) => v.isDefault);
    const rest = savedViews.filter((v) => !v.isDefault);
    return (def ? [def, ...rest] : rest).slice(0, MAX_SAVED_VIEWS);
  }, [savedViews]);
  const quickViewButtons = quickViews.map((v) => (
    <button key={v.id} type="button" className="view-quick-btn" onClick={() => applyView(v)} title={`Go to “${v.name}”`}>
      {v.isDefault && '★ '}
      {v.name}
    </button>
  ));

  // Open the account's default view once per signed-in account per page load.
  // Later changes to the default only take effect on the next load.
  const defaultAppliedFor = useRef<number | null>(null);
  useEffect(() => {
    if (!user || defaultAppliedFor.current === user.id) return;
    defaultAppliedFor.current = user.id;
    let cancelled = false;
    listViews()
      .then((views) => {
        const def = views.find((v) => v.isDefault);
        if (def && !cancelled) applyView(def, true);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // applyView only calls setters and a ref, so it is safe to leave out.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const signOut = async () => {
    await auth.signOut();
    setDraft(null);
    setPicking(false);
    setTab('reports');
  };

  const newest = health?.newestSightingAt ?? null;
  const isStale = health?.stale ?? false;
  const windowLabel = timeMode === 'date' ? `on ${formatDateKey(dateKey)}` : `in the last ${trailLabel(trailHours)}`;

  // The whole app requires an account — there is no anonymous view of the
  // map. While the initial /api/me check is in flight, show nothing rather
  // than flashing the login screen for anyone who already has a session.
  // Once it resolves, no account means the gate instead of the app; every
  // hook above this point still runs regardless (Rules of Hooks), but this
  // is the only point from here down where `user` narrows to non-null, so
  // everything below can and does rely on that (see e.g. the Places tab).
  if (!auth.ready) return null;
  if (!user) return <LoginGate registrationOpen={auth.registrationOpen} onAuthed={auth.apply} />;
  // An admin expired this account's password (Accounts, in the admin page) —
  // the server enforces this too (requireActiveUser blocks every other route
  // until it's cleared), so this isn't just a client-side suggestion.
  if (user.passwordExpired) {
    return <PasswordExpiredGate username={user.username} onChanged={auth.apply} onSignOut={() => void signOut()} />;
  }
  // A full page, not a sidebar tab (see the Settings tab's "Open admin page"
  // button) — no map alongside it, so onFlyTo closes it and returns to the
  // map instead (showOnMap).
  if (adminPageOpen) {
    return (
      <AdminPage
        onClose={() => setAdminPageOpen(false)}
        onFlyTo={showOnMap}
        onChanged={(n) => {
          setPendingCount(n);
          loadViewing();
        }}
      />
    );
  }

  return (
    <div className="app">
      <header className="header">
        {/* Two pre-rendered PNGs (not a themed inline SVG) because this is the
            brand lockup from /assets, not an icon this app draws itself; the
            source picks the dark-background variant purely by OS preference,
            matching every other light/dark switch in this app (see
            useColorScheme's doc comment) — there is no manual theme toggle. */}
        <picture className="brand">
          <source srcSet="/whaletail-horizontal-dark.png" media="(prefers-color-scheme: dark)" />
          <img src="/whaletail-horizontal.png" alt="WhaleTail" height={28} />
        </picture>

        <span className="spacer" />

        {isAdmin && unplacedCount > 0 && (
          <button
            type="button"
            className="unplaced-indicator"
            onClick={() => setAdminPageOpen(true)}
            title={`${unplacedCount} Orca Network text${unplacedCount === 1 ? '' : 's'} with a place the map could not find — open the admin page to review`}
            aria-label={`${unplacedCount} unplaced Orca Network text${unplacedCount === 1 ? '' : 's'}. Open the admin page.`}
          >
            {unplacedCount}
          </button>
        )}

        <span className={`freshness${isStale ? ' stale' : ''}`}>
          <span className="dot" />
          {newest ? `Last report ${ago(now - newest)}` : 'No recent reports'}
        </span>

        {/* No "Sign in" button here any more: the whole app requires an
            account (see the LoginGate branch below), so user is always
            set by the time this header renders. Account info and Sign
            out live at the top of the Settings tab. */}
      </header>

      {error && (
        <div className="notice" role="alert">
          <strong>Offline.</strong>&nbsp;{error} — showing the last data loaded.
        </div>
      )}

      <div className="main" data-panel={panelOpen ? 'open' : 'collapsed'} data-dragging={sheetH !== null}>
        <div className="map-wrap">
          <MapView
            ref={mapRef}
            tracks={visible}
            live={livePositions}
            // Whether ANY track has ever been fetched, independent of the
            // selected time window — see MapView's doc comment on this prop
            // for why the one-time initial auto-fit keys off it instead of
            // the windowed `tracks`/`live` props.
            hasFetchedTracks={tracks.length > 0}
            initialFocus={pendingFocus}
            selectedId={selectedId}
            onSelect={setSelectedId}
            trailHours={fadeHours}
            windowEnd={win.end}
            viewingLocations={viewingLocations}
            showViewing={showViewing}
            home={user.home}
            alertRadiusMiles={alertRadiusMiles}
            myLocation={geo.position}
            draft={draft}
            onDraftMove={(p) => setDraft((d) => ({ ...p, label: d?.label ?? null }))}
            pickMode={picking}
            onPick={(p) => {
              setDraft({ ...p, label: null });
              setPicking(false);
            }}
          />

          {picking && (
            <div className="pick-banner" role="status">
              Click the map to place the pin
              <button className="btn btn-small" onClick={() => setPicking(false)}>
                Cancel
              </button>
            </div>
          )}

          {notice && !picking && (
            <div className="toast" role="status" aria-live="polite" key={notice.id}>
              {notice.text}
              <button className="icon-btn" onClick={() => setNotice(null)} aria-label="Dismiss">
                ×
              </button>
            </div>
          )}

          <div className="map-tools">
            <button
              className="fit-btn"
              aria-pressed={showViewing}
              data-on={String(showViewing)}
              onClick={() => setShowViewing((v) => !v)}
              title={`${showViewing ? 'Hide' : 'Show'} ${viewingLocations.length} shore viewing spots`}
            >
              View points
            </button>
            <button
              className="fit-btn fit-icon"
              aria-pressed={geo.status === 'on'}
              data-on={String(geo.status === 'on')}
              data-busy={String(geo.status === 'locating')}
              onClick={geo.toggle}
              aria-label={geo.status === 'off' ? 'Show my location' : 'Stop showing my location'}
              title={
                geo.status === 'off'
                  ? 'Show my location on the map. It stays on your device and is never sent to the server.'
                  : geo.status === 'locating'
                    ? 'Finding you… (click to cancel)'
                    : 'Stop showing my location'
              }
            >
              <PersonIcon size={16} />
            </button>
            {/* Desktop: saved views stack under the tools. Phones show the
                same buttons in a row above the time bar instead (.map-views). */}
            {quickViews.length > 0 && (
              <div className="map-tools-views">
                <span className="map-tools-label">Saved Views</span>
                {quickViewButtons}
              </div>
            )}
          </div>

          {/* One bottom stack, so the phone's saved-view row always sits right
              above the time bar however tall the bar wraps. */}
          <div className="map-bottom">
            {quickViews.length > 0 && (
              <div className="map-views" role="group" aria-label="Saved views">
                {quickViewButtons}
              </div>
            )}
            <TimeControls
              mode={timeMode}
              onModeChange={changeMode}
              live={live && timeMode === 'hours'}
              onLive={goLive}
              windowEnd={win.end}
              sliderMin={win.sliderMin}
              sliderMax={win.sliderMax}
              onScrub={scrub}
              trailHours={trailHours}
              trailOptions={TRAIL_OPTIONS}
              onTrailChange={changeTrail}
              dateKey={dateKey}
              dateMin={dateMin}
              dateMax={dateMax}
              onDateChange={chooseDate}
              reportCount={logRows.length}
            />
          </div>
        </div>

        <aside
          className="side"
          data-panel-open={panelOpen}
          ref={sideRef}
          style={{ '--sheet-h': `${sheetHeight}px` } as CSSProperties}
        >
          {/* Mobile-only (see .side-handle's display rule): the side panel sits
              at the bottom of the screen there and starts collapsed to just
              this tab row, so it doesn't eat into the map. Swiping the handle or
              tab row up pulls it up and swiping down collapses it (swipeProps);
              picking a tab (selectTab) pulls it up too, since choosing one
              implies wanting to see it. The handle stays a button so keyboard
              users can still toggle it. */}
          <button
            type="button"
            className="side-handle"
            aria-expanded={panelOpen}
            aria-controls="side-body"
            aria-label={panelOpen ? 'Collapse the panel' : 'Expand the panel'}
            onClick={(e) => {
              // detail === 0 is a keyboard activation; a touch/mouse tap is not a toggle.
              if (e.detail === 0) setPanelOpen((o) => !o);
            }}
            {...swipeProps}
          >
            <span className="side-handle-grip" aria-hidden="true" />
          </button>

          <div className="tabs" role="tablist" aria-label="Side panel" {...swipeProps}>
            <button role="tab" aria-selected={tab === 'reports'} className="tab" onClick={() => selectTab('reports')}>
              Reports
            </button>
            <button role="tab" aria-selected={tab === 'places'} className="tab" onClick={() => selectTab('places')}>
              Settings
            </button>
          </div>

          <div className="side-body" id="side-body">
            {tab === 'reports' && (
              <ReportLog
                rows={logRows}
                loading={loading}
                selectedId={selectedId}
                onSelect={setSelectedId}
                windowLabel={windowLabel}
                empty={
                  <>
                    {timeMode === 'date' ? `No reports on ${formatDateKey(dateKey)}.` : `No reports in the last ${trailLabel(trailHours)}.`}
                    <br />
                    Sightings are reported in daylight, so quiet stretches are normal.
                    {suggestion?.kind === 'hours' && (
                      <>
                        <br />
                        <button className="chip" style={{ marginTop: 10 }} onClick={() => changeTrail(suggestion.hours)}>
                          Show the last {trailLabel(suggestion.hours)} instead
                        </button>
                      </>
                    )}
                    {suggestion?.kind === 'day' && (
                      <>
                        <br />
                        <button className="chip" style={{ marginTop: 10 }} onClick={() => chooseDate(suggestion.key)}>
                          Jump to {formatDateKey(suggestion.key)} ({suggestion.count} reports)
                        </button>
                      </>
                    )}
                  </>
                }
              />
            )}

            {/* No signed-out branch here any more: the whole app requires an
                account (see the LoginGate branch below), so user is always
                set by the time this tab can even be selected. */}
            {tab === 'places' && (
              <PlacesPanel
                user={user}
                onUserChange={auth.setUser}
                onSignedOut={() => void signOut()}
                captureView={captureView}
                onApplyView={applyView}
                draft={draft}
                onDraftChange={setDraft}
                picking={picking}
                onPickingChange={setPicking}
                myLocation={geo.position}
                onWantMyLocation={geo.start}
                onFlyTo={flyTo}
                onViewpointsChanged={loadViewing}
                onViewsChanged={setSavedViews}
                notify={notify}
                onOpenAdminPage={() => setAdminPageOpen(true)}
                pendingReviewCount={pendingCount}
                onAlertsChange={applyAlerts}
              />
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
